import {
    useEffect,
    useMemo,
    type FC,
    useRef
} from "react";
import {
    type TextStyle,
    Animated,
    Platform,
    Text
} from "react-native";
import type ITraceShimmerTextProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    MOTION_EASINGS
} from "../../../../helpers/motion";

const TraceShimmerText: FC<ITraceShimmerTextProps> = ({
    customTheme,
    children,
    style,
    ...props
}) => {
    const {
        typography,
        durations,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const isWeb = Platform.OS === "web";

    const progress = useRef(new Animated.Value(0)).current;

    const {
        webShimmer: webShimmerDynamicStyle
    } = useStyles({
        from: colors.content.text.mid,
        to: colors.content.text.high,
        duration: durations.loop
    });

    const color = useMemo(() => progress.interpolate({
        outputRange: [
            colors.content.text.mid,
            colors.content.text.high
        ],
        inputRange: [
            0,
            1
        ]
    }), [
        colors.content.text.high,
        colors.content.text.mid,
        progress
    ]);

    useEffect(() => {
        if(isWeb) {
            return;
        }

        const duration = durations.loop / 2;

        const animation = Animated.loop(Animated.sequence([
            Animated.timing(progress, {
                easing: MOTION_EASINGS.linear,
                useNativeDriver: false,
                isInteraction: false,
                toValue: 1,
                duration
            }),
            Animated.timing(progress, {
                easing: MOTION_EASINGS.linear,
                useNativeDriver: false,
                isInteraction: false,
                toValue: 0,
                duration
            })
        ]));

        animation.start();

        return () => {
            animation.stop();
        };
    }, [
        durations.loop,
        progress,
        isWeb
    ]);

    if(isWeb) {
        return <Text
            {...props}
            style={[
                stylesheet.text,
                style,
                typography.bodyMediumSize as TextStyle,
                webShimmerDynamicStyle
            ]}
            numberOfLines={1}
        >
            {children}
        </Text>;
    }

    return <Animated.Text
        {...props}
        style={[
            stylesheet.text,
            style,
            typography.bodyMediumSize as TextStyle,
            {
                color
            }
        ]}
        numberOfLines={1}
    >
        {children}
    </Animated.Text>;
};
export default TraceShimmerText;
